/**
 * Minimap - Norse-styled radar/minimap for Midgard
 * 
 * Shows:
 * - Player position and heading
 * - Nearby multiplayer players (respecting spatial relevance)
 * - Roads, landmarks, and settlements
 * - Current area name
 * - Active waypoint direction
 */

import { useMemo } from 'react';
import { MapPin, Navigation, User, Home, Mountain, Castle, Flame } from 'lucide-react';

// Minimap configuration
const MINIMAP_CONFIG = {
  size: 140,           // Size in pixels
  worldScale: 0.25,    // World units per pixel (1px = 4 world units shown)
  playerRadius: 100,   // Show entities within this radius
  padding: 16,
};

// Known landmarks/settlements for display on minimap
const LANDMARKS = [
  { id: 'thornhaven', name: 'Thornhaven', position: { x: 0, z: 0 }, type: 'settlement', icon: 'castle' },
  { id: 'standing-stones', name: 'Standing Stones', position: { x: -15, z: -190 }, type: 'landmark', icon: 'mountain' },
  { id: 'watchtower', name: 'Ruined Watchtower', position: { x: 18, z: -320 }, type: 'landmark', icon: 'mountain' },
  { id: 'bridge', name: 'Stone Bridge', position: { x: 0, z: -430 }, type: 'landmark', icon: 'home' },
  { id: 'camp', name: 'Abandoned Camp', position: { x: -12, z: -495 }, type: 'landmark', icon: 'flame' },
  { id: 'frostholm', name: 'Frostholm', position: { x: 0, z: -660 }, type: 'settlement', icon: 'castle' },
];

// Road segments for minimap
const ROAD_SEGMENTS = [
  { from: { x: 0, z: 25 }, to: { x: 0, z: -700 } }, // Main road
];

// Area names based on Z position
function getAreaName(z) {
  if (z > -30) return 'Thornhaven';
  if (z > -200) return 'Central Plains';
  if (z > -350) return 'Northern Woodland';
  if (z > -480) return 'Rocky Highlands';
  if (z > -560) return 'Snow Transition';
  if (z > -620) return 'Northern Frost';
  return 'Frostholm';
}

/**
 * Convert world coordinates to minimap coordinates
 */
function worldToMinimap(worldX, worldZ, playerX, playerZ, scale, size) {
  const relX = (worldX - playerX) * scale;
  const relZ = (worldZ - playerZ) * scale;
  
  // Center of minimap is at size/2, size/2
  const mapX = size / 2 + relX;
  const mapY = size / 2 - relZ; // Flip Z for screen coords (north = up)
  
  return { x: mapX, y: mapY };
}

/**
 * Check if a point is within the visible minimap area
 */
function isInMinimapBounds(x, y, size, padding = 5) {
  return x >= padding && x <= size - padding && y >= padding && y <= size - padding;
}

/**
 * Minimap Component
 */
export default function Minimap({
  playerPosition = { x: 0, z: 0 },
  playerRotation = 0,
  remotePlayers = [],
  waypoint = null,
  onWaypointClick,
  className = '',
}) {
  const { x: px, z: pz } = playerPosition;
  const size = MINIMAP_CONFIG.size;
  const scale = MINIMAP_CONFIG.worldScale;
  
  // Current area name
  const areaName = useMemo(() => getAreaName(pz), [pz]);
  
  // Calculate visible landmarks
  const visibleLandmarks = useMemo(() => {
    return LANDMARKS.map(landmark => {
      const pos = worldToMinimap(landmark.position.x, landmark.position.z, px, pz, scale, size);
      const distance = Math.sqrt(
        Math.pow(landmark.position.x - px, 2) + 
        Math.pow(landmark.position.z - pz, 2)
      );
      return {
        ...landmark,
        mapPos: pos,
        distance,
        visible: isInMinimapBounds(pos.x, pos.y, size, 10),
      };
    }).filter(l => l.visible || l.distance < MINIMAP_CONFIG.playerRadius * 2);
  }, [px, pz, scale, size]);
  
  // Calculate visible remote players
  const visiblePlayers = useMemo(() => {
    return remotePlayers
      .filter(player => player.currentPosition)
      .map(player => {
        const pos = worldToMinimap(
          player.currentPosition.x, 
          player.currentPosition.z, 
          px, pz, scale, size
        );
        const distance = Math.sqrt(
          Math.pow(player.currentPosition.x - px, 2) + 
          Math.pow(player.currentPosition.z - pz, 2)
        );
        return {
          ...player,
          mapPos: pos,
          distance,
          visible: isInMinimapBounds(pos.x, pos.y, size),
        };
      })
      .filter(p => p.visible && p.distance < MINIMAP_CONFIG.playerRadius);
  }, [remotePlayers, px, pz, scale, size]);
  
  // Calculate road line visible on minimap
  const roadLine = useMemo(() => {
    const segment = ROAD_SEGMENTS[0];
    const from = worldToMinimap(segment.from.x, segment.from.z, px, pz, scale, size);
    const to = worldToMinimap(segment.to.x, segment.to.z, px, pz, scale, size);
    return { from, to };
  }, [px, pz, scale, size]);
  
  // Calculate waypoint position if set
  const waypointPos = useMemo(() => {
    if (!waypoint) return null;
    const pos = worldToMinimap(waypoint.x, waypoint.z, px, pz, scale, size);
    const distance = Math.sqrt(Math.pow(waypoint.x - px, 2) + Math.pow(waypoint.z - pz, 2));
    const angle = Math.atan2(waypoint.x - px, waypoint.z - pz);
    return { ...pos, distance, angle, inBounds: isInMinimapBounds(pos.x, pos.y, size) };
  }, [waypoint, px, pz, scale, size]);
  
  // Player heading arrow rotation (convert from game rotation to CSS rotation)
  // Game: 0 = north (positive Z), increasing clockwise
  // CSS: 0 = up, increasing clockwise
  const playerHeadingDeg = (playerRotation * 180 / Math.PI);
  
  // Get icon for landmark type
  const getLandmarkIcon = (type, iconName) => {
    switch (iconName) {
      case 'castle': return <Castle className="w-3 h-3" />;
      case 'mountain': return <Mountain className="w-3 h-3" />;
      case 'home': return <Home className="w-3 h-3" />;
      case 'flame': return <Flame className="w-3 h-3" />;
      default: return <MapPin className="w-3 h-3" />;
    }
  };
  
  return (
    <div 
      className={`flex flex-col items-center ${className}`}
      data-testid="minimap"
    >
      {/* Area Name */}
      <div className="mb-1 px-2 py-0.5 rounded bg-black/60 backdrop-blur-sm border border-amber-900/40">
        <span className="text-amber-200 text-xs font-medium">{areaName}</span>
      </div>
      
      {/* Minimap Container */}
      <div 
        className="relative rounded-full overflow-hidden border-2 border-amber-900/60 shadow-lg"
        style={{ 
          width: size, 
          height: size,
          background: 'radial-gradient(circle, #1a2f1a 0%, #0f1a0f 70%, #080d08 100%)',
        }}
      >
        {/* Norse-style decorative border */}
        <div 
          className="absolute inset-0 rounded-full border-4 border-amber-800/30 pointer-events-none"
          style={{ 
            boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5)',
          }}
        />
        
        {/* Compass ring */}
        <div className="absolute inset-2 rounded-full border border-amber-900/20" />
        
        {/* Cardinal directions */}
        <span className="absolute top-1 left-1/2 -translate-x-1/2 text-[10px] text-amber-600/60 font-bold">N</span>
        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] text-amber-600/40">S</span>
        <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[10px] text-amber-600/40">W</span>
        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-amber-600/40">E</span>
        
        {/* Road line */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          <line
            x1={roadLine.from.x}
            y1={roadLine.from.y}
            x2={roadLine.to.x}
            y2={roadLine.to.y}
            stroke="rgba(139, 90, 43, 0.4)"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
        
        {/* Landmarks */}
        {visibleLandmarks.map(landmark => (
          <div
            key={landmark.id}
            className={`absolute transform -translate-x-1/2 -translate-y-1/2 ${
              landmark.type === 'settlement' ? 'text-amber-400' : 'text-amber-600/70'
            }`}
            style={{ left: landmark.mapPos.x, top: landmark.mapPos.y }}
            title={landmark.name}
          >
            {getLandmarkIcon(landmark.type, landmark.icon)}
          </div>
        ))}
        
        {/* Remote players */}
        {visiblePlayers.map(player => (
          <div
            key={player.playerId}
            className="absolute w-2 h-2 rounded-full bg-blue-400 border border-blue-200 transform -translate-x-1/2 -translate-y-1/2"
            style={{ left: player.mapPos.x, top: player.mapPos.y }}
            title={player.displayName || 'Player'}
          />
        ))}
        
        {/* Waypoint indicator */}
        {waypointPos && (
          waypointPos.inBounds ? (
            <div
              className="absolute w-3 h-3 transform -translate-x-1/2 -translate-y-1/2"
              style={{ left: waypointPos.x, top: waypointPos.y }}
            >
              <MapPin className="w-3 h-3 text-emerald-400 animate-pulse" />
            </div>
          ) : (
            // Show direction arrow at edge when waypoint is out of bounds
            <div
              className="absolute w-4 h-4 transform -translate-x-1/2 -translate-y-1/2 text-emerald-400"
              style={{ 
                left: size / 2 + Math.sin(waypointPos.angle) * (size / 2 - 12),
                top: size / 2 - Math.cos(waypointPos.angle) * (size / 2 - 12),
                transform: `translate(-50%, -50%) rotate(${waypointPos.angle * 180 / Math.PI}deg)`,
              }}
            >
              <Navigation className="w-4 h-4" />
            </div>
          )
        )}
        
        {/* Player indicator (center) */}
        <div 
          className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
          style={{ transform: `translate(-50%, -50%) rotate(${playerHeadingDeg}deg)` }}
        >
          {/* Player arrow */}
          <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-b-[10px] border-l-transparent border-r-transparent border-b-amber-400" />
        </div>
        
        {/* Player dot */}
        <div className="absolute left-1/2 top-1/2 w-2 h-2 rounded-full bg-amber-400 border border-amber-200 transform -translate-x-1/2 -translate-y-1/2" />
      </div>
      
      {/* Waypoint distance */}
      {waypointPos && (
        <div className="mt-1 px-2 py-0.5 rounded bg-emerald-900/40 backdrop-blur-sm border border-emerald-700/30">
          <span className="text-emerald-300 text-[10px]">
            {Math.round(waypointPos.distance)}m to waypoint
          </span>
        </div>
      )}
    </div>
  );
}
